<template>
    <div class="container mx-auto">
        <div class="mt-6 flex flex-wrap items-center justify-between gap-y-7">
            <h1
                class="order-2 w-full text-center text-[36px] lg:order-2 lg:w-auto lg:text-[40px] xl:text-[50px]"
            >
                Stickers & Labels
            </h1>
            <div class="order-1 inline-flex w-1/2 lg:w-1/4">
                <a
                    href="#intro"
                    class="inline-flex gap-2 font-semibold text-grind hover:text-brew"
                >
                    <img
                        src="~/assets/images/product-finder/back-arrow.svg"
                        alt="Back arrow"
                        width="33"
                        height="24"
                    />
                    Back
                </a>
            </div>
            <div
                class="order-2 inline-flex w-1/2 justify-end lg:order-3 lg:w-1/4"
            ></div>
        </div>
        <div class="flex flex-col items-center justify-center gap-8 px-4 py-8">
            <div class="mt-4">
                <Alert
                    v-if="state.showErrors"
                    id="configurator-error"
                    type="error"
                    class="mb-8"
                    :dismissable="true"
                    slot-class="wysiwyg-content wysiwyg-compact"
                    @dismiss="$emit('dismissErrors')"
                >
                    <p>Make the following selections to continue:</p>

                    <ul>
                        <li
                            v-for="message in state.validationMessages"
                            :key="message"
                        >
                            {{ message }}
                        </li>
                    </ul>
                </Alert>
            </div>
            <h3 class="text-center text-2xl lg:w-auto">
                {{ designPriceSticker.stickerQuestion }}
            </h3>
            <div class="lg:px-6">
                <div
                    class="mt-4 grid grid-cols-2 justify-start gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-3"
                >
                    <ConfiguratorButtonImage
                        v-for="selection in stickerSelections"
                        :key="selection.value"
                        :name="selection.value"
                        :label="selection.label"
                        :image-url="getStickerImage(selection.value)"
                        :selected="state.currentSticker === selection.value"
                        height-class="lg:h-40"
                        width-class="lg:w-40"
                        :use-story-blok-image="true"
                        @click="
                            onStickerClick(
                                selection.value,
                                selection.minimumQuantity,
                            )
                        "
                    />
                </div>
            </div>

            <div class="flex justify-center">
                <button
                    class="btn-primary mt-6 inline-flex items-center justify-center gap-2 md:order-2"
                    @click="onContinueClick()"
                >
                    Continue
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { DesignPriceStickerType } from '@/types/DesignPriceType';
import ConfiguratorButtonImage from '~/components/configurator/ConfiguratorButtonImage.vue';
import { stickerSelections } from '~/utils/designprice';
import { scrollToTop } from '~/utils/helpers';

const props = defineProps<{
    designPriceSticker: DesignPriceStickerType;
}>();

let showErrors;

const state = reactive<{
    currentSticker?: string;
    currentQuantity?: number;
    currentDesignOption?: string;
    minimumQuantity: number;
    showErrors: boolean;
    validationMessages: string[];
}>({
    currentSticker: undefined,
    currentQuantity: 0,
    currentDesignOption: 'upload_your_own',
    minimumQuantity: 0,
    showErrors: false,
    validationMessages: [],
});

const onStickerClick = (sticker: string, minimumQuantity: number) => {
    state.currentSticker = sticker;
    state.minimumQuantity = minimumQuantity;
    onCheckErrors();
};

const onCheckErrors = () => {
    state.showErrors = false;
    state.validationMessages = [];

    if (!state.currentSticker) {
        state.showErrors = true;
        state.validationMessages.push('Please select a sticker or label');
    }
};

const onContinueClick = () => {
    onCheckErrors();

    if (state.showErrors) {
        scrollToTop();
        return;
    }

    if (!state.currentSticker) {
        showErrors = true;
        return;
    }

    if (
        state.currentSticker == 'bag-label' ||
        state.currentSticker == 'beverage-label'
    ) {
        navigateTo(
            '/get-quote?productType=sticker&type=' + state.currentSticker,
        );
    } else {
        navigateTo(
            '/get-quote?productType=sticker&type=sticker&shape=' +
                state.currentSticker,
        );
    }
};

const getStickerImage = (stickerType: string) => {
    if (stickerType === 'custom') {
        return props.designPriceSticker.customDieCutStickersImage.filename;
    } else if (stickerType === 'circle-or-oval') {
        return props.designPriceSticker.circleOvalStickerImage.filename;
    } else if (stickerType === 'square-or-rectangle') {
        return props.designPriceSticker.squareRectangleStickerImage.filename;
    } else if (stickerType === 'bag-label') {
        return props.designPriceSticker.coffeeBagLabelsImage.filename;
    } else if (stickerType === 'beverage-label') {
        return props.designPriceSticker.beverageLabelImage.filename;
    }
};
</script>
